import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';

import { Observable, EMPTY } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { AuthService } from '../services/auth/auth.service';
import { UserDataService } from '../services/data/user-data.service';

import { UserModel } from '../models/user.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private userDataService: UserDataService,
    private router: Router
  ) {}

  private hasRedirected = false;

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<CognitoUserSession>> {
    const user = this.userDataService.getUser();
    const currentUrl = this.router.url;
    const usernameFromUrl = this.getUsernameFromUrl(currentUrl);

    const isUploadRequest =
      (request.url.includes('image/user/profile') || request.url.includes('image/user/banner')) &&
      request.method === 'POST';

    if (this.isPublicRequest(request, user, currentUrl, usernameFromUrl)) {
      return next.handle(request.clone());
    }

    return this.authService.getCurrentUserSession().pipe(
      take(1),
      switchMap((auth: CognitoUserSession | null) => {
        if (!auth) {
          this.authService.signOut();
          if (!this.hasRedirected) {
            this.hasRedirected = true;
          }

          return EMPTY;
        }

        const jwt = auth.getIdToken().getJwtToken();
        const isPatch = request?.method === 'PATCH';

        let authReq;
        if (isUploadRequest) {
          authReq = request.clone({
            setHeaders: {
              authorization: `Bearer ${jwt}`,
            },
          });
        } else {
          authReq = request.clone({
            setHeaders: {
              'content-type': isPatch ? 'application/json-patch+json' : 'application/json',
              authorization: `Bearer ${jwt}`,
            },
          });
        }

        return next.handle(authReq);
      })
    );
  }

  private getUsernameFromUrl(url: string): string | null {
    const match = url.match(/^.*\/badminton-profile\/([^\/\?]+)$/);
    return match ? match[1] : null;
  }

  private isPublicRequest(
    request: HttpRequest<any>,
    user: UserModel | null,
    currentUrl: string,
    usernameFromUrl: string
  ): boolean {
    const url = request.url.toLowerCase();
    const method = request.method;

    // Public GETs
    if (url.includes('venue') && method === 'GET') return true;
    if (url.includes('counter') && method === 'GET') return true;
    if ((url.includes('type=all') || url.includes('type=group')) && method === 'GET') return true;
    if (url.includes('/sponsor') && method === 'GET') return true;
    if (url.includes('v1/member') && method === 'GET') return true;

    //if (url.includes('v1/group/') && !url.includes('type=mine') && method === 'GET') return true;
    if (url.includes('/game/group/') && method === 'GET') return true;
    if (url.includes('v1/leaderboard?spots=3') && method === 'GET') return true;

    if (
      currentUrl.match(/^.*\/badminton-profile\/[^\/\?]+$/) &&
      url.endsWith('/user') &&
      user?.firstName != usernameFromUrl &&
      method === 'GET'
    )
      return true;

    // Allow group/:id or event/:groupId/:eventId when no user
    if (!user && url.match(/^.*\/group\/[^\/\?]+$/) && method === 'GET') return true;
    if (!user && url.match(/^.*\/event\/[^\/]+\/[^\/]+$/) && method === 'GET') return true;

    // Allow subscription? if no user
    if (!user && url.includes('subscription?') && (method === 'GET' || method === 'PUT'))
      return true;

    return false;
  }
}
